import React from 'react';
import ExecDashboardSource from 'sources/ExecDashboardSource';
import {List, Map} from 'immutable';
import UserSource from 'sources/UserSource';
import {associateBy} from 'utils/associateBy';
import LoadingOverlay from 'components/pure/utils/LoadingOverlay';
import UserAvatar from 'components/pure/utils/UserAvatar';
import classNames from 'classnames';
import GenericTooltip from 'components/pure/tooltips/GenericTooltip';
import {safePercent, toHHMM} from 'components/dashboard/admin/admin-coaching-meetings/index';
import {ImprovementMetricDisplay} from 'components/dashboard/admin/admin-coaching-meetings/components/ImprovementMetricDisplay';
import {MeetingCategoryTooltip} from 'components/dashboard/admin/admin-coaching-meetings/components/MeetingCategoryTooltip';
import './MeetingMetricsCard.styl';
import {SquaredOffBar} from 'components/dashboard/admin/admin-coaching-meetings/components/SquaredOffBar';
import buildUrl from 'components/dashboard/buildUrl';
import {browserHistory} from 'react-router';
import AuthStore from 'stores/AuthStore';
import GenericLink from 'components/GenericLink';
import {HISTORY_ACTIONS, TOOLTIP_DELAY} from 'components/StatsPage2.utils';
import {CircleXIcon} from '@components/icon/CircleXIcon';
import {GroupIcon} from '@components/icon/GroupIcon';
import {getMuiV4Theme} from 'utils/css/themeUtils';

export class MeetingMetricsCard extends React.Component {
  constructor(props) {
    super(props);

    this.state = this.DEFAULT_STATE;
    this.loadPopover = this.loadPopover.bind(this);
    this.onPopoverClose = this.onPopoverClose.bind(this);
  }

  DEFAULT_STATE = {
    loadedPopover: false,
    loadingPopover: false,
    showingPopover: false,
    userCoachingBreakdown: undefined,
    improvementBreakdown: undefined,
  };

  async loadPopover() {
    const {teamIds, coach, startDate, endDate, onPopoverOpen} = this.props;

    if (this.state.loadedPopover) {
      onPopoverOpen(coach.id);
      this.setState({
        showingPopover: true,
      });
      return;
    }

    this.setState({
      loadedPopover: false,
      loadingPopover: true,
    });

    const [userCoaching, improvementBreakdown, scoringBreakdown] = await Promise.all([
      ExecDashboardSource.getUserMetric({
        teams: teamIds,
        userId: coach.id,
        direction: 'coach',
        startDate,
        endDate,
        metric: 'meeting_category_time',
      }),
      ExecDashboardSource.getUserMetric({
        teams: teamIds,
        userId: coach.id,
        direction: 'coach',
        startDate,
        endDate,
        metric: 'coaching_plan_improvement',
      }),
      ExecDashboardSource.getUserMetric({
        teams: teamIds,
        userId: coach.id,
        direction: 'coach',
        startDate,
        endDate,
        metric: 'scoring',
      }),
    ]);

    const userCoachingBreakdown = List(userCoaching).groupBy(ucb => ucb.id);

    const popoverUsers = await UserSource.fetchAll({
      ids: userCoachingBreakdown.keySeq(),
    });

    this.setState({
      userCoachingBreakdown: userCoachingBreakdown,
      improvementBreakdown: Map(improvementBreakdown.map(i => [i.id, i.value])),
      scoringBreakdown: Map(scoringBreakdown.map(i => [i.id, i.value])),
      popoverUsers: associateBy(popoverUsers, u => u.id),
      loadedPopover: true,
      loadingPopover: false,
      showingPopover: true,
    });

    onPopoverOpen(coach.id);
  }

  onPopoverClose() {
    this.setState({
      showingPopover: false,
    });

    this.props.onPopoverClose && this.props.onPopoverClose();
  }

  render() {
    const {
      coach,
      meetingCategories,
      maxMeetingCategoryTime,
      meetingCategoryTimeInSeconds,
      meetingCategoryCount,
      meetingTime,
      planCount,
      meetingCount,
      improvement,
      left,
      fromPath,
      popped,
      coachNameClick,
    } = this.props;

    const {
      userCoachingBreakdown,
      improvementBreakdown,
      scoringBreakdown,
      popoverUsers,
      loadedPopover,
      loadingPopover,
      showingPopover,
    } = this.state;

    const hoverable = meetingCategoryTimeInSeconds.find(it => it.value > 0);
    const adminId = AuthStore.getState().user.id;

    // pure "coach" only - not a team manager
    const isOnlyCoach = !coach.isTeamManager;

    const goTo = (path, adminDash = false, extraQueryParams = {}) => {
      const query = {
        ...extraQueryParams,
      };
      const state = {
        ...(fromPath
          ? {
              pathname: fromPath,
              action: HISTORY_ACTIONS.PUSH,
            }
          : {}),
      };

      const basePath = adminDash
        ? buildUrl({adminId})
        : buildUrl({
            adminId,
            managerId: !isOnlyCoach ? coach.id : undefined,
            coachId: isOnlyCoach ? coach.id : undefined,
          });
      browserHistory.push({
        pathname: basePath + path,
        state,
        query,
      });
    };

    const card = (
      <div>
        {loadingPopover && <LoadingOverlay />}
        <div className="header">
          <div className="user">
            <UserAvatar user={coach} />
            <h2>
              <GenericLink onClick={() => coachNameClick(coach)}>{coach.getFullName()}</GenericLink>
            </h2>
          </div>
          <div className="details">
            {hoverable && (
              <div className="more-container">
                <GenericTooltip
                  clickBehavior
                  blurToClose={false}
                  delay={TOOLTIP_DELAY}
                  className={classNames('rep-level-tooltip', left ? 'left' : 'right')}
                  positionRight={left}
                  positionLeft={!left}
                  onOpen={this.loadPopover}
                  onClose={this.onPopoverClose}
                  tooltip={
                    loadedPopover ? (
                      <MeetingCategoryTooltip
                        userId={coach.id}
                        meetingCategories={meetingCategories}
                        userCoachingBreakdown={userCoachingBreakdown}
                        improvementBreakdown={improvementBreakdown}
                        scoringBreakdown={scoringBreakdown}
                        users={popoverUsers}
                        fromPath={fromPath}
                      />
                    ) : null
                  }>
                  {showingPopover ? (
                    <CircleXIcon fill={'rgb(228, 74, 156)'} tooltip="Close" />
                  ) : (
                    <GroupIcon fill={getMuiV4Theme().palette.primary.main} tooltip="Details" />
                  )}
                </GenericTooltip>
              </div>
            )}
          </div>
        </div>
        <div className="body">
          {meetingCategories.toList().map(mc => {
            const timeMetric = meetingCategoryTimeInSeconds.find(
              it => parseInt(it.context, 10) === mc.id
            );

            const countMetric = meetingCategoryCount.find(it => parseInt(it.context, 10) === mc.id);
            const pct = safePercent(timeMetric ? timeMetric.value : 0, maxMeetingCategoryTime);

            return (
              <div key={mc.id} className="row">
                <span className="row-label">{mc.name}</span>
                <SquaredOffBar
                  pct={pct}
                  color={mc.color}
                  label={timeMetric && timeMetric.value > 0 ? toHHMM(timeMetric.value) : ''}
                  labelColor={pct < 10 ? '#424242' : 'white'}
                />
                <span className="count-label">
                  {countMetric && countMetric.value > 0 ? countMetric.value : ''}
                </span>
              </div>
            );
          })}
        </div>
        <div className="footer">
          <div
            className="metric-container"
            onClick={() => goTo('/coaching-plans', true, {tabIndex: 0, coachId: coach.id})}>
            <div className="metric">{meetingCount}</div>
            <div className="label">Meetings</div>
          </div>
          <div
            className="metric-container"
            onClick={() => goTo('/coaching-plans', false, {scrollToPreviouslyCoached: true})}>
            <div className="metric">{planCount}</div>
            <div className="label">Plans</div>
          </div>
          <div
            className="metric-container"
            onClick={() => goTo('/coaching-plans', false, {scrollToPreviouslyCoached: true})}>
            <ImprovementMetricDisplay improvement={improvement} className="metric" />
            <div className="label">Avg Rep Improv.</div>
          </div>
          <div className="metric-container" onClick={() => goTo('/coaching-effectiveness')}>
            <div className="metric">{toHHMM(meetingTime)}</div>
            <div className="label">Meeting Time</div>
          </div>
        </div>
      </div>
    );

    if (hoverable) {
      return <div className={classNames('meeting-metrics-card', popped && 'popped')}>{card}</div>;
    }

    return <div className={classNames('meeting-metrics-card', popped && 'popped')}>{card}</div>;
  }
}
